import { useMemo } from "react";
import { AxiosError } from "axios";
import { useMutation } from "react-query";
import catalog from "@granular/fabric-catalog";

import activities from "../../../redux/activities";
import { MutationEnvelope, ensureMutationState } from "../../../services";
import { User } from "../../../models";
import { getAuthToken } from "../../../services/granularAxios";
import { tripInsightsDataDirtyFlag } from "../../../services/insights";

import { useSetUserGlobalActivityAndStatus } from "./user.globalStatus";
import { useInvalidateUser } from "./user.invalidate";

// ###################
// ## useUpdateUser ##
// ###################

export type ResultOfUseUpdateUser = MutationEnvelope<User>;
export type UseUpdateUser = () => ResultOfUseUpdateUser;

/**
 * Use the `mutate` function returned from this hook to update the current user.
 * This will trigger an invalidation of the user data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useUpdateUser: UseUpdateUser = () => {
  const invalidateUser = useInvalidateUser();

  const setUserGlobalActivityAndStatus = useSetUserGlobalActivityAndStatus();

  const catalogConfig = {
    getAuthToken,
  };

  const { data, error, status, mutateAsync } = useMutation<
    User,
    AxiosError,
    User
  >(
    (user) =>
      catalog.user
        .updateUser({ user, userRequestConfig: catalogConfig })
        .then((r) => r.data),
    {
      onMutate: () => {
        setUserGlobalActivityAndStatus({
          status: "loading",
          activity: activities.updateUser,
        });
      },
      onSuccess: async () => {
        tripInsightsDataDirtyFlag();

        await Promise.all([invalidateUser()]);

        setUserGlobalActivityAndStatus({
          status: "success",
          activity: activities.updateUser,
        });
      },
      onError: () => {
        setUserGlobalActivityAndStatus({
          status: "error",
          activity: activities.updateUser,
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};
